import { useEffect, useRef, useState } from "react";
import { apiClient } from "@/lib/api/client";
import type { ItineraryItem, Trip } from "@/lib/api/schemas";

export interface SwapAlternative {
  title: string;
  placeName?: string;
  description: string;
  reasoning?: string;
  cost?: string;
  duration?: string;
  lat?: number;
  lng?: number;
  address?: string;
  rating?: number;
}

interface SwapPopoverProps {
  tripId: string;
  item: ItineraryItem;
  onClose: () => void;
  onApplied: (trip: Trip) => void;
}

// Popover that fetches 3 LLM-generated alternatives for the given item
// and lets the user pick one to replace it with.
export function SwapPopover({
  tripId,
  item,
  onClose,
  onApplied,
}: SwapPopoverProps) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [alternatives, setAlternatives] = useState<SwapAlternative[] | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const fetchAlternatives = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.post<{
        alternatives: SwapAlternative[];
      }>(`/v1/trips/${tripId}/items/${item.id}/suggest-swap`, {
        reason: reason.trim(),
      });
      setAlternatives(result.alternatives || []);
    } catch (err: any) {
      setError(err?.message || "Failed to fetch alternatives");
    } finally {
      setLoading(false);
    }
  };

  const apply = async (alt: SwapAlternative) => {
    setApplying(true);
    try {
      const updated = await apiClient.post<Trip>(
        `/v1/trips/${tripId}/items/${item.id}/apply-swap`,
        { alternative: alt }
      );
      onApplied(updated);
      onClose();
    } catch (err: any) {
      setError(err?.message || "Failed to apply swap");
      setApplying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-deep/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        ref={containerRef}
        className="relative w-full max-w-xl max-h-[85vh] overflow-y-auto rounded-2xl frost-panel shadow-2xl page-fade"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-white/10 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-light">
              Suggest alternative
            </div>
            <div className="text-lg font-semibold text-ink mt-0.5">
              {item.title}
            </div>
            {item.placeName && item.placeName !== item.title && (
              <div className="text-sm text-ink-light mt-0.5">
                {item.placeName}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full bg-deep/60 border border-white/20 text-ink hover:bg-deep/80"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!alternatives && (
            <>
              <div>
                <label className="text-sm text-ink-light block mb-1">
                  Why do you want to swap this? (optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. 'too touristy', 'allergies', 'looking for something more local'"
                  rows={2}
                  className="w-full px-3 py-2 border border-white/20 bg-white/5 text-ink rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-accent text-sm placeholder:text-ink-light"
                />
              </div>
              <button
                onClick={fetchAlternatives}
                disabled={loading}
                className="w-full px-4 py-2.5 bg-accent text-deep rounded-lg font-semibold hover:bg-accent-light disabled:opacity-50 text-sm"
              >
                {loading ? "Thinking..." : "Get 3 alternatives"}
              </button>
              {error && (
                <p className="text-sm text-red-300 bg-red-400/10 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
            </>
          )}

          {alternatives && (
            <>
              <div className="space-y-3">
                {alternatives.map((alt, i) => (
                  <AlternativeCard
                    key={i}
                    alt={alt}
                    disabled={applying}
                    onPick={() => apply(alt)}
                  />
                ))}
              </div>
              {error && (
                <p className="text-sm text-red-300 bg-red-400/10 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
              <button
                onClick={() => {
                  setAlternatives(null);
                  setError(null);
                }}
                disabled={applying}
                className="text-xs text-ink-light hover:text-ink underline"
              >
                ← Try again with different input
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AlternativeCard({
  alt,
  disabled,
  onPick,
}: {
  alt: SwapAlternative;
  disabled: boolean;
  onPick: () => void;
}) {
  return (
    <div className="frost-card rounded-lg p-4 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-ink">{alt.title}</h4>
          {alt.placeName && alt.placeName !== alt.title && (
            <p className="text-xs text-ink-light mt-0.5">{alt.placeName}</p>
          )}
        </div>
        {alt.rating ? (
          <span className="text-xs text-warm shrink-0">
            ★ {alt.rating.toFixed(1)}
          </span>
        ) : null}
      </div>
      {alt.description && (
        <p className="text-sm text-ink-light">{alt.description}</p>
      )}
      {alt.reasoning && (
        <p className="text-xs text-accent-light italic">💡 {alt.reasoning}</p>
      )}
      <div className="flex gap-3 text-xs text-ink-light">
        {alt.cost && <span>{alt.cost}</span>}
        {alt.duration && <span>{alt.duration}</span>}
        {alt.address && <span className="truncate">{alt.address}</span>}
      </div>
      <button
        onClick={onPick}
        disabled={disabled}
        className="w-full mt-1 px-3 py-1.5 bg-accent/80 hover:bg-accent text-deep rounded-md text-sm font-semibold disabled:opacity-50 transition-colors"
      >
        Swap to this
      </button>
    </div>
  );
}
